import React, { useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import OtpInput from "./OTPInput";
import { generateOTPSms, loginWithMobile } from "../../services/authService";
import { storeInSession } from "../../common/session";
import { useNavigate } from "react-router-dom";
import { UserVerifyOTPModel, UserLoginResultModel } from "../../types/user";

const PhoneOTP: React.FC = () => {
  const [mobile, setMobile] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [showOtpInput, setShowOtpInput] = useState<boolean>(false);
  const [hashValue, setHashValue] = useState<string>("");

  const navigate = useNavigate();

  const handleSendOtp = async () => {
    if (!/^\d{10}$/.test(mobile)) {
      toast.error("Enter a valid 10-digit mobile number");
      return;
    }

    setLoading(true);
    try {
      // 1?? Generate OTP (backend now also sends SMS)
      const response: UserVerifyOTPModel | null = await generateOTPSms(mobile);
      if (!response) throw new Error("Failed to generate OTP");

      const { hashvalue, otp, mobile: formattedMobile } = response;
      setHashValue(hashvalue);

      toast.success(`OTP sent to ${formattedMobile}`);
      setShowOtpInput(true);

    } catch (error: any) {
      console.error("Error generating OTP:", error);
      toast.error(error.message || "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (enteredOtp: string) => {
    if (!enteredOtp) {
      toast.error("Please enter the OTP");
      return;
    }

    setLoading(true);
    try {
      const response: UserLoginResultModel | null = await loginWithMobile(
        mobile,
        enteredOtp,
        hashValue
      );

      if (response && response.loginResult === 1) {
        storeInSession("user", response);
        toast.success("Login successful!");
        navigate("/");
      } else {
        toast.error("Invalid OTP or login failed.");
      }
    } catch (error: any) {
      console.error("OTP verification failed:", error);
      toast.error(error.message || "OTP verification failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Toaster />
      <div className="flex items-center justify-center h-screen ">
        {!showOtpInput ? (
          <div className="w-full max-w-md p-6 bg-white rounded-lg shadow-md">
            <h2 className="mb-6 text-2xl font-bold text-center text-gray-800">
              Login with OTP
            </h2>
            <input
              type="text"
              placeholder="Enter your mobile number"
              value={mobile}
              maxLength={10}
              onChange={(e) => setMobile(e.target.value)}
              className="block w-full px-4 py-2 mb-4 border rounded-lg"
            />
            <button
              onClick={handleSendOtp}
              disabled={loading}
              className={`w-full py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 ${
                loading ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              {loading ? "Sending..." : "Send OTP"}
            </button>
          </div>
        ) : (
          <div className="w-full max-w-md p-6 bg-white rounded-lg shadow-md">
            <h2 className="mb-6 text-2xl font-bold text-center text-gray-800">
              Enter OTP
            </h2>
            <OtpInput length={6} mobile={mobile} onOtpSubmit={handleVerifyOtp} />
          </div>
        )}
      </div>
    </>
  );
};

export default PhoneOTP;
